// Tankeez - Complete multiplayer client
// Supports: Quick Match (Best of 5 online), FFA (4-player), Tournament (16-player)

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ===== WebSocket URL =====
const WS_URL = 'wss://tankeez.com/ws';

/* ===== CONSTANTS ===== */
const AW = 900, AH = 600, TS = 40;
const BS = 8, BSPD = 5, MS = 16;
const MDEL = 3000, TSPD = 3.5, SCD = 350;
const MCD = 2000, BLT = 3000;
const MAXBNC = 1, MAXB = 5;

const UPGRADES = [
  {id:'fastShoot',name:'Rapid Fire',desc:'Shoot 40% faster',icon:'⚡'},
  {id:'bigBullets',name:'Heavy Rounds',desc:'Larger bullets',icon:'💥'},
  {id:'launchMines',name:'Mine Launcher',desc:'Launch mines forward',icon:'🚀'},
  {id:'fasterTank',name:'Turbo Engine',desc:'Move 30% faster',icon:'🏎️'},
  {id:'shieldDrop',name:'Shield Generator',desc:'Drop shields instead of mines',icon:'🛡️'},
  {id:'extraBounce',name:'Ricochet Master',desc:'Bullets bounce 2 extra times',icon:'🔄'},
  {id:'ghostMines',name:'Stealth Mines',desc:'Mines invisible to enemies',icon:'👻'},
  {id:'quickMines',name:'Quick Fuse',desc:'Mines explode 50% faster',icon:'⏱️'},
  {id:'doubleTap',name:'Double Barrel',desc:'Shoot two bullets at once',icon:'🎯'},
  {id:'armorPiercing',name:'Piercing Shots',desc:'Bullets pass through bullets',icon:'🗡️'},
  {id:'extraBullets',name:'Extended Magazine',desc:'8 bullets on map instead of 5',icon:'🔋'},
  {id:'fastBullets',name:'Velocity Boost',desc:'Bullets travel 40% faster',icon:'💨'},
  {id:'tankArmor',name:'Reactive Armor',desc:'Survive one hit (one-time)',icon:'🛡️'},
  {id:'mineLayer',name:'Mine Deployer',desc:'Drop 2 mines at once',icon:'💣'},
  {id:'longRange',name:'Extended Range',desc:'Bullets bounce 1 extra time',icon:'📏'},
  {id:'scatterShot',name:'Scatter Shot',desc:'Shoot 3 bullets in a spread',icon:'🌟'},
  {id:'homingMines',name:'Proximity Mines',desc:'Mines explode near enemies',icon:'📡'},
  {id:'quickReload',name:'Quick Reload',desc:'Bullets bounce 1 less time',icon:'🔃'},
  {id:'bigMines',name:'Mega Mines',desc:'50% larger explosion radius',icon:'☢️'},
  {id:'ghostTank',name:'Cloaking Device',desc:'Semi-transparent while moving',icon:'🌫️'},
];

/* ===== HELPERS ===== */
const uid = () => Math.random().toString(36).substr(2, 9);
const dst = (x1,y1,x2,y2) => Math.sqrt((x2-x1)*(x2-x1)+(y2-y1)*(y2-y1));
const normA = (a) => { while(a>Math.PI) a-=2*Math.PI; while(a<-Math.PI) a+=2*Math.PI; return a; };
const hitC = (x1,y1,r1,x2,y2,r2) => dst(x1,y1,x2,y2) < r1+r2;
const cl = (v,lo,hi) => Math.max(lo,Math.min(hi,v));
const has = (u,id) => u && u.includes(id);
const rndUp = (n,ex) => { const av=UPGRADES.filter(u=>!(ex||[]).includes(u.id)); return [...av].sort(()=>Math.random()-0.5).slice(0,Math.min(n,av.length)); };
const bName = () => { const p=['Dark','Steel','Thunder','Shadow','Iron','Blaze','Storm','Frost','Cyber','Mega'],s=['Tank','Crusher','Destroyer','Hunter','Warrior','Striker','Blaster','Cannon','Gunner','Titan']; return p[Math.floor(Math.random()*p.length)]+s[Math.floor(Math.random()*s.length)]; };

function genObs() {
  const obs=[],n=5+Math.floor(Math.random()*4);
  for(let i=0;i<n;i++){const iv=Math.random()>0.5;const w=iv?20:60+Math.random()*80,h=iv?60+Math.random()*80:20;
    let x,y,ok,att=0;do{ok=true;x=120+Math.random()*(AW-240-w);y=120+Math.random()*(AH-240-h);
    if(dst(x+w/2,y+h/2,80,AH/2)<180)ok=false;if(dst(x+w/2,y+h/2,AW-80,AH/2)<180)ok=false;
    for(const o of obs){if(x<o.x+o.width+80&&x+w+80>o.x&&y<o.y+o.height+80&&y+h+80>o.y){ok=false;break;}}att++;}while(!ok&&att<50);
    if(ok)obs.push({x,y,width:w,height:h,id:uid()});}return obs;
}
function mkBullets(tank){const u=tank.upgrades||[];let cnt=1,spr=0;if(has(u,'scatterShot')){cnt=3;spr=0.25;}else if(has(u,'doubleTap')){cnt=2;spr=0.1;}
  const mx=has(u,'extraBullets')?8:MAXB,own=tank._obc||0;cnt=Math.min(cnt,mx-own);if(cnt<=0)return null;
  const spd=BSPD*(has(u,'fastBullets')?1.4:1);
  // Calculate max bounces: base 1 + extraBounce gives +2, longRange gives +1, quickReload gives -1 (min 0)
  let maxB=MAXBNC;if(has(u,'extraBounce'))maxB+=2;if(has(u,'longRange'))maxB+=1;if(has(u,'quickReload'))maxB=Math.max(0,maxB-1);
  const sz=has(u,'bigBullets')?BS*1.5:BS,batchId=uid(),out=[];
  for(let i=0;i<cnt;i++){const off=cnt>1?(i-(cnt-1)/2)*spr:0,a=tank.turretAngle+off;
    out.push({id:uid(),x:tank.x+Math.cos(a)*(TS/2+10),y:tank.y+Math.sin(a)*(TS/2+10),vx:Math.cos(a)*spd,vy:Math.sin(a)*spd,ownerId:tank.id,bounces:0,createdAt:Date.now(),upgrades:[...u],size:sz,piercing:has(u,'armorPiercing'),maxBounces:maxB,batchId});}return out;}
function mkMines(tank){const u=tank.upgrades||[];if(has(u,'shieldDrop'))return{type:'shield',items:[{id:uid(),x:tank.x-Math.cos(tank.angle)*30,y:tank.y-Math.sin(tank.angle)*30,createdAt:Date.now(),ownerId:tank.id}]};
  const launch=has(u,'launchMines'),cnt=has(u,'mineLayer')?2:1,items=[];
  for(let i=0;i<cnt;i++){const off=cnt>1?(i-0.5)*40:0,perp=tank.angle+Math.PI/2;let mx,my;
    if(launch){mx=tank.x+Math.cos(tank.turretAngle)*100+Math.cos(perp)*off;my=tank.y+Math.sin(tank.turretAngle)*100+Math.sin(perp)*off;}
    else{mx=tank.x-Math.cos(tank.angle)*30+Math.cos(perp)*off;my=tank.y-Math.sin(tank.angle)*30+Math.sin(perp)*off;}
    items.push({id:uid(),x:cl(mx,MS,AW-MS),y:cl(my,MS,AH-MS),createdAt:Date.now(),ownerId:tank.id,upgrades:[...u],invisible:has(u,'ghostMines'),proximity:has(u,'homingMines'),bigExplosion:has(u,'bigMines')});}
  return{type:'mine',items};}

/* ===== BOT AI (offline only) ===== */
class BotAI{constructor(d){this.d=Math.min(1,0.3+(d||0.5)*0.7);this.ev=Math.random()>0.5?1:-1;this.ls=0;this.lm=0;this.ldc=0;}
  update(bot,enemy,bullets,mines,obstacles,now){const o={moveX:0,moveY:0,shoot:false,mine:false,turretTarget:0};if(!enemy)return o;
    if(now-this.ldc>1500+Math.random()*1000){this.ev*=-1;this.ldc=now;}const dx=enemy.x-bot.x,dy=enemy.y-bot.y,dd=Math.sqrt(dx*dx+dy*dy),toE=Math.atan2(dy,dx);
    o.turretTarget=toE+(Math.random()-0.5)*(1-this.d)*0.2;const ideal=220+(1-this.d)*80;
    if(dd>ideal+60){o.moveX=Math.cos(toE)*0.7;o.moveY=Math.sin(toE)*0.7;}else if(dd<ideal-60){o.moveX=-Math.cos(toE)*0.7;o.moveY=-Math.sin(toE)*0.7;}
    let danger=0,evX=0,evY=0;for(const b of bullets){const bd=dst(bot.x,bot.y,b.x,b.y);if(bd>200)continue;const ba=Math.atan2(b.vy,b.vx);
      for(let t=1;t<=15;t++){if(dst(bot.x,bot.y,b.x+b.vx*t,b.y+b.vy*t)<TS+20){const pp=ba+Math.PI/2*this.ev,ug=Math.max(0.3,1-bd/150);evX+=Math.cos(pp)*ug*1.5;evY+=Math.sin(pp)*ug*1.5;danger+=ug;break;}}
      if(bd<80){const aw=Math.atan2(bot.y-b.y,bot.x-b.x);evX+=Math.cos(aw)*0.5;evY+=Math.sin(aw)*0.5;danger+=0.3;}}
    if(danger>0){o.moveX=evX;o.moveY=evY;}for(const m of mines){const md=dst(bot.x,bot.y,m.x,m.y);if(md<120){const aw=Math.atan2(bot.y-m.y,bot.x-m.x),s=Math.min(1.5,100/md);o.moveX+=Math.cos(aw)*s;o.moveY+=Math.sin(aw)*s;}}
    if(danger<0.3){const sa=toE+Math.PI/2*this.ev;o.moveX+=Math.cos(sa)*0.4;o.moveY+=Math.sin(sa)*0.4;}
    const mg=Math.sqrt(o.moveX*o.moveX+o.moveY*o.moveY);if(mg>1){o.moveX/=mg;o.moveY/=mg;}
    const mxB=has(bot.upgrades,'extraBullets')?8:MAXB,ownB=bullets.filter(bl=>bl.ownerId===bot.id).length;
    if(now-this.ls>800-this.d*400&&ownB<mxB-1&&danger<0.5){if(Math.abs(normA(o.turretTarget-bot.turretAngle))<0.15+(1-this.d)*0.2){if(this._safe(bot,obstacles)){o.shoot=true;this.ls=now;}}}
    if(now-this.lm>4000&&danger<0.3&&Math.random()<this.d*0.25&&dd<180&&dd>100){o.mine=true;this.lm=now;}return o;}
  _safe(bot,obs){const a=bot.turretAngle;let sx=bot.x+Math.cos(a)*(TS/2+10),sy=bot.y+Math.sin(a)*(TS/2+10),svx=Math.cos(a)*BSPD,svy=Math.sin(a)*BSPD,bn=0;
    const mxB=MAXBNC+(has(bot.upgrades,'extraBounce')?2:0);
    for(let s=0;s<40;s++){sx+=svx;sy+=svy;if(sx<BS||sx>AW-BS){if(bn<mxB){svx*=-1;bn++;}else break;}if(sy<BS||sy>AH-BS){if(bn<mxB){svy*=-1;bn++;}else break;}
      for(const o of obs){if(sx>o.x&&sx<o.x+o.width&&sy>o.y&&sy<o.y+o.height){if(bn<mxB){svx*=-1;svy*=-1;bn++;}else break;}}if(s>5&&dst(sx,sy,bot.x,bot.y)<TS+15)return false;}return true;}}

/* ===== OFFLINE GAME TICK ===== */
function offlineTick(world,input){const{obstacles,botAIs}=world;const{keys,mousePos,mouseButtons,canvasRect,now}=input;
  const addExp=(x,y,r)=>world.explosions.push({id:uid(),x,y,radius:r,createdAt:now,checked:false});
  for(const tank of world.tanks){if(!tank.alive)continue;const spd=TSPD*(has(tank.upgrades,'fasterTank')?1.3:1);
    if(tank.isBot){const ai=botAIs[tank.id],en=world.tanks.find(t=>t.id!==tank.id&&t.alive);
      if(ai&&en){const act=ai.update(tank,en,world.bullets,world.mines,obstacles,now);tank.x+=act.moveX*spd;tank.y+=act.moveY*spd;
        tank.isMoving=Math.abs(act.moveX)>0.1||Math.abs(act.moveY)>0.1;if(tank.isMoving)tank.angle=Math.atan2(act.moveY,act.moveX);
        if(act.turretTarget!==undefined)tank.turretAngle+=normA(act.turretTarget-tank.turretAngle)*0.1;
        if(act.shoot){tank._obc=world.bullets.filter(b=>b.ownerId===tank.id).length;const bs=mkBullets(tank);if(bs)world.bullets.push(...bs);}
        if(act.mine){const r=mkMines(tank);if(r.type==='shield')world.shields.push(...r.items);else world.mines.push(...r.items);}
    }}else{let mx=0,my=0;if(keys['w']||keys['arrowup'])my-=1;if(keys['s']||keys['arrowdown'])my+=1;if(keys['a']||keys['arrowleft'])mx-=1;if(keys['d']||keys['arrowright'])mx+=1;
      const mg=Math.sqrt(mx*mx+my*my);tank.isMoving=mg>0;if(mg>0){mx/=mg;my/=mg;tank.x+=mx*spd;tank.y+=my*spd;tank.angle=Math.atan2(my,mx);}
      if(canvasRect){const sx=AW/canvasRect.width,sy=AH/canvasRect.height;tank.turretAngle=Math.atan2(mousePos.y*sy-tank.y,mousePos.x*sx-tank.x);}
      const cd=SCD*(has(tank.upgrades,'fastShoot')?0.6:1);if(mouseButtons.left&&now-(tank._lastShot||0)>cd){tank._obc=world.bullets.filter(b=>b.ownerId===tank.id).length;const bs=mkBullets(tank);if(bs){world.bullets.push(...bs);tank._lastShot=now;}}
      if(mouseButtons.right&&now-(tank._lastMine||0)>MCD){const r=mkMines(tank);if(r.type==='shield')world.shields.push(...r.items);else world.mines.push(...r.items);tank._lastMine=now;}}
    tank.x=cl(tank.x,TS/2,AW-TS/2);tank.y=cl(tank.y,TS/2,AH-TS/2);
    for(const ob of obstacles){const cx=cl(tank.x,ob.x,ob.x+ob.width),cy=cl(tank.y,ob.y,ob.y+ob.height),d=dst(tank.x,tank.y,cx,cy);if(d<TS/2){const pa=Math.atan2(tank.y-cy,tank.x-cx);tank.x=cx+Math.cos(pa)*(TS/2+1);tank.y=cy+Math.sin(pa)*(TS/2+1);}}}
  // Bullet movement - use maxBounces from bullet, no time expiration
  for(let i=world.bullets.length-1;i>=0;i--){const b=world.bullets[i];b.x+=b.vx;b.y+=b.vy;
    const mxB=b.maxBounces!==undefined?b.maxBounces:MAXBNC;
    if(b.x<BS||b.x>AW-BS){if(b.bounces<mxB){b.vx*=-1;b.bounces++;b.x=cl(b.x,BS,AW-BS);}else{world.bullets.splice(i,1);continue;}}
    if(b.y<BS||b.y>AH-BS){if(b.bounces<mxB){b.vy*=-1;b.bounces++;b.y=cl(b.y,BS,AH-BS);}else{world.bullets.splice(i,1);continue;}}
    let rem=false;for(const ob of obstacles){if(b.x>ob.x&&b.x<ob.x+ob.width&&b.y>ob.y&&b.y<ob.y+ob.height){if(b.bounces<mxB){const px=b.x-b.vx,py=b.y-b.vy;if(px<=ob.x||px>=ob.x+ob.width)b.vx*=-1;if(py<=ob.y||py>=ob.y+ob.height)b.vy*=-1;b.bounces++;}else{world.bullets.splice(i,1);rem=true;break;}}}if(rem)continue;}
  // Bullet-bullet collision
  for(let i=world.bullets.length-1;i>=0;i--){for(let j=i-1;j>=0;j--){const a=world.bullets[i],b=world.bullets[j];if(!a||!b)continue;if(a.batchId&&a.batchId===b.batchId)continue;
    if(hitC(a.x,a.y,a.size,b.x,b.y,b.size)){if(!a.piercing){world.bullets.splice(i,1);i--;}if(!b.piercing){world.bullets.splice(j,1);i--;}break;}}}
  // Bullet-mine collision
  for(let i=world.bullets.length-1;i>=0;i--){const b=world.bullets[i];if(!b)continue;for(let j=world.mines.length-1;j>=0;j--){const m=world.mines[j];if(hitC(b.x,b.y,b.size,m.x,m.y,MS)){addExp(m.x,m.y,m.bigExplosion?90:60);world.mines.splice(j,1);world.bullets.splice(i,1);break;}}}
  // Mine timer/proximity
  for(let j=world.mines.length-1;j>=0;j--){const m=world.mines[j];const dl=MDEL*(has(m.upgrades,'quickMines')?0.5:1),r=m.bigExplosion?90:60;let ex=now-m.createdAt>dl;
    if(!ex&&m.proximity){for(const t of world.tanks){if(!t.alive||t.id===m.ownerId)continue;if(dst(m.x,m.y,t.x,t.y)<50){ex=true;break;}}}if(ex){addExp(m.x,m.y,r);world.mines.splice(j,1);}}
  // Shield expiration
  for(let i=world.shields.length-1;i>=0;i--)if(now-world.shields[i].createdAt>5000)world.shields.splice(i,1);
  // Bullet-shield collision
  for(let i=world.bullets.length-1;i>=0;i--){const b=world.bullets[i];if(!b)continue;for(let j=world.shields.length-1;j>=0;j--){if(hitC(b.x,b.y,b.size,world.shields[j].x,world.shields[j].y,30)){world.shields.splice(j,1);world.bullets.splice(i,1);break;}}}
  // Bullet-tank collision - reactive armor absorbs hit without killing
  for(let i=world.bullets.length-1;i>=0;i--){const b=world.bullets[i];if(!b)continue;for(const t of world.tanks){if(!t.alive)continue;if(hitC(b.x,b.y,b.size,t.x,t.y,TS/2)){world.bullets.splice(i,1);if(t.hasArmor){t.hasArmor=false;t._armorJustBroken=true;addExp(t.x,t.y,40);}else{t.alive=false;addExp(t.x,t.y,80);}break;}}}
  // Explosion damage - skip tanks whose armor just broke this tick
  for(const e of world.explosions){if(e.checked||now-e.createdAt>100)continue;for(const t of world.tanks){if(!t.alive||t._armorJustBroken)continue;if(hitC(e.x,e.y,e.radius,t.x,t.y,TS/2)){if(t.hasArmor){t.hasArmor=false;t._armorJustBroken=true;addExp(t.x,t.y,40);}else{t.alive=false;addExp(t.x,t.y,80);}}}e.checked=true;}
  // Clear armor-just-broken flag
  for(const t of world.tanks){t._armorJustBroken=false;}
  for(let i=world.explosions.length-1;i>=0;i--)if(now-world.explosions[i].createdAt>500)world.explosions.splice(i,1);
  return{alive:world.tanks.filter(t=>t.alive)};}

/* ===== OFFLINE TOURNAMENT ===== */
function createTourn(name){const pl=[{id:'player',name,isBot:false,upgrades:[]}];for(let i=1;i<8;i++)pl.push({id:'bot_'+i,name:bName(),isBot:true,upgrades:[],difficulty:0.3+Math.random()*0.5});
  const sh=[...pl].sort(()=>Math.random()-0.5);const br=[];for(let i=0;i<sh.length;i+=2)br.push({player1:sh[i],player2:sh[i+1],winner:null});return{players:sh,bracket:br,round:1,roundHistory:[],champion:null};}
function simBots(bracket){return bracket.map(m=>{if(m.winner||!m.player1.isBot||!m.player2.isBot)return m;const d1=m.player1.difficulty||0.5,d2=m.player2.difficulty||0.5,w=Math.random()<d1/(d1+d2)?m.player1:m.player2;const u=rndUp(1,w.upgrades)[0];if(u)w.upgrades=[...w.upgrades,u.id];return{...m,winner:w};});}
function findPM(bracket){return bracket.find(m=>!m.winner&&(!m.player1.isBot||!m.player2.isBot));}
function advanceTourn(tourn,winner){let nb=tourn.bracket.map(m=>{const is=(m.player1.id===winner.id||m.player2.id===winner.id);return(is&&!m.winner)?{...m,winner}:m;});nb=simBots(nb);const hist=[...tourn.roundHistory,nb],ws=nb.map(m=>m.winner);
  if(ws.length<=1)return{tournament:{...tourn,bracket:[],champion:ws[0],roundHistory:hist},phase:'victory'};const next=[];for(let i=0;i<ws.length;i+=2)if(ws[i+1])next.push({player1:ws[i],player2:ws[i+1],winner:null});
  return{tournament:{...tourn,bracket:simBots(next),round:tourn.round+1,roundHistory:hist},phase:'bracket'};}

/* ===== BRACKET DISPLAY ===== */
function BracketDisplay({bracket,roundHistory,round,champion,playerName,title}){
  const allR=roundHistory||[];const cur=bracket||[];
  const renderName=(name,isW,isL)=>{const me=name===playerName;return(<div style={{padding:'5px 0',color:isW?'#00ff88':isL?'#e74c3c':'#e0e0e0',fontWeight:(me||isW)?700:400,fontSize:14,textDecoration:isL?'line-through':'none',opacity:isL?0.6:1}}>{me?'⭐ ':''}{name}</div>);};
  const matchBox=(m,hl)=>{
    const inProgress = !m.winner && cur.includes(m);
    return (<div style={{background:hl?'rgba(0,212,255,0.1)':'rgba(0,20,40,0.8)',border:'2px solid '+(inProgress?'#ffaa00':hl?'#00d4ff':'#3a4a6a'),borderRadius:8,padding:'10px 15px',minWidth:140,boxShadow:hl?'0 0 20px rgba(0,255,255,0.3)':inProgress?'0 0 15px rgba(255,170,0,0.4)':'none',animation:inProgress?'pulse 1.5s ease infinite':'none'}}>
    {inProgress && <div style={{fontSize:10,color:'#ffaa00',fontWeight:600,marginBottom:5,letterSpacing:1}}>⚔️ IN PROGRESS</div>}
    {renderName(m.player1.name,m.winner&&m.winner.id===m.player1.id,m.winner&&m.winner.id!==m.player1.id)}<div style={{borderTop:'1px solid #3a4a6a'}}/>
    {renderName(m.player2.name,m.winner&&m.winner.id===m.player2.id,m.winner&&m.winner.id!==m.player2.id)}</div>);
  };
  return(<div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:30,padding:20,width:'100%',maxWidth:1000}}>
    <h2 style={{fontSize:28,fontWeight:700,color:'#00d4ff',margin:0,letterSpacing:3}}>{title||'TOURNAMENT BRACKET'}</h2>
    <div style={{display:'flex',gap:40,alignItems:'center',justifyContent:'center',flexWrap:'wrap'}}>
      {allR.map((r,ri)=>(<div key={ri} style={{display:'flex',flexDirection:'column',gap:20,alignItems:'center'}}><div style={{fontSize:14,color:'#7890a0',fontWeight:600,letterSpacing:2}}>ROUND {ri+1}</div>{r.map((m,mi)=><div key={mi}>{matchBox(m,false)}</div>)}</div>))}
      {cur.length>0&&!champion&&(<div style={{display:'flex',flexDirection:'column',gap:20,alignItems:'center'}}><div style={{fontSize:14,color:'#00d4ff',fontWeight:600,letterSpacing:2}}>ROUND {round}{cur.some(m=>!m.winner)?' (CURRENT)':''}</div>{cur.map((m,mi)=><div key={mi}>{matchBox(m,m.player1.name===playerName||m.player2.name===playerName)}</div>)}</div>)}
      {champion&&(<div style={{display:'flex',flexDirection:'column',gap:20,alignItems:'center'}}><div style={{fontSize:14,color:'#ffd700',fontWeight:600,letterSpacing:2}}>CHAMPION</div>
        <div style={{background:'linear-gradient(135deg,rgba(255,215,0,0.2),rgba(255,170,0,0.1))',border:'2px solid #ffd700',borderRadius:8,padding:'15px 25px',textAlign:'center'}}><div style={{fontSize:32,marginBottom:5}}>🏆</div><div style={{color:'#ffd700',fontWeight:700,fontSize:16}}>{champion.name}</div></div></div>)}
    </div></div>);}

/* ===== CSS ===== */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
*{box-sizing:border-box}
@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.05);opacity:0.8}}
@keyframes slideIn{from{transform:translateY(-20px);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes explosion{0%{transform:scale(0);opacity:1}100%{transform:scale(1);opacity:0}}
@keyframes countdown{0%{transform:scale(2);opacity:0}20%{transform:scale(1);opacity:1}80%{transform:scale(1);opacity:1}100%{transform:scale(0.5);opacity:0}}
@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
.btn{background:linear-gradient(135deg,#00d4ff,#0099cc);border:none;padding:15px 40px;font-size:18px;font-family:'Orbitron',sans-serif;font-weight:700;color:#0a0a0a;cursor:pointer;border-radius:4px;text-transform:uppercase;letter-spacing:2px;transition:all 0.3s}
.btn:hover{transform:translateY(-2px);box-shadow:0 10px 30px rgba(0,212,255,0.4)}.btn:disabled{background:#444;color:#888;cursor:not-allowed;transform:none;box-shadow:none}
.btn-alt{background:linear-gradient(135deg,#ff6b35,#cc4400)}.btn-alt:hover{box-shadow:0 10px 30px rgba(255,107,53,0.4)}
.btn-green{background:linear-gradient(135deg,#00ff88,#00cc66)}.btn-green:hover{box-shadow:0 10px 30px rgba(0,255,136,0.4)}
.ucard{background:linear-gradient(145deg,#1e2a4a,#152035);border:2px solid #00d4ff;border-radius:12px;padding:25px;cursor:pointer;transition:all 0.3s;width:280px}
.ucard:hover{transform:translateY(-5px) scale(1.02);border-color:#00ff88;box-shadow:0 15px 40px rgba(0,255,136,0.3)}
input[type="text"]{background:rgba(0,20,40,0.8);border:2px solid #00d4ff;padding:15px 20px;font-size:18px;font-family:'Rajdhani',sans-serif;color:#e0e0e0;border-radius:4px;outline:none;width:300px;transition:all 0.3s}
input[type="text"]:focus{border-color:#00ff88;box-shadow:0 0 20px rgba(0,255,136,0.3)}input[type="text"]::placeholder{color:#607080}
.spinner{width:40px;height:40px;border:4px solid #3a4a6a;border-top-color:#00d4ff;border-radius:50%;animation:spin 0.8s linear infinite}
`;

/* ===== UPGRADE ICON WITH TOOLTIP ===== */
function UpgradeIcon({id}) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const u = UPGRADES.find(x => x.id === id);
  if (!u) return null;
  return (
    <div 
      style={{position:'relative',display:'inline-block'}}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div style={{background:'rgba(0,20,40,0.8)',padding:'8px 12px',borderRadius:4,border:'1px solid #00ff88',fontSize:16,cursor:'help'}}>
        {u.icon}
      </div>
      {showTooltip && (
        <div style={{
          position:'absolute',
          bottom:'100%',
          left:'50%',
          transform:'translateX(-50%)',
          marginBottom:8,
          background:'rgba(0,20,40,0.95)',
          border:'2px solid #00ff88',
          borderRadius:8,
          padding:'10px 15px',
          minWidth:180,
          zIndex:1000,
          textAlign:'center',
          boxShadow:'0 4px 20px rgba(0,255,136,0.3)'
        }}>
          <div style={{fontSize:24,marginBottom:5}}>{u.icon}</div>
          <div style={{color:'#00ff88',fontWeight:700,fontSize:14,marginBottom:4}}>{u.name}</div>
          <div style={{color:'#7890a0',fontSize:12,fontFamily:"'Rajdhani',sans-serif"}}>{u.desc}</div>
          <div style={{position:'absolute',bottom:-6,left:'50%',transform:'translateX(-50%)',width:0,height:0,borderLeft:'6px solid transparent',borderRight:'6px solid transparent',borderTop:'6px solid #00ff88'}}/>
        </div>
      )}
    </div>
  );
}

/* ===== ARENA RENDERER ===== */
function Arena({tanks,bullets,mines,shields,explosions,obstacles,cvRef,battleWinner,myId,playerUps,currentRound,pBC,pMB,opponent,battleTimer,overtime,seriesWins,winsNeeded,onlinePlayers,isTournament,isFFA}) {
  const myWins = seriesWins && myId ? (seriesWins[myId] || 0) : 0;
  const oppPlayer = onlinePlayers && !isFFA ? onlinePlayers.find(p => p.id !== myId) : null;
  const oppWins = seriesWins && oppPlayer ? (seriesWins[oppPlayer.id] || 0) : 0;
  
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',padding:20,gap:15}}>
      <div style={{display:'flex',justifyContent:'space-between',width:'100%',maxWidth:AW,padding:'0 10px'}}>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {isTournament ? (
            <div style={{background:'rgba(0,20,40,0.8)',padding:'10px 20px',borderRadius:4,border:'1px solid #00d4ff'}}>
              <span style={{color:'#7890a0',fontSize:12}}>ROUND </span><span style={{color:'#00d4ff',fontSize:18,fontWeight:700}}>{currentRound}</span></div>
          ) : isFFA ? (
            <div style={{display:'flex',gap:8}}>
              {(onlinePlayers||[]).map(p => (
                <div key={p.id} style={{background:p.id===myId?'rgba(0,255,136,0.2)':'rgba(0,20,40,0.8)',padding:'6px 12px',borderRadius:4,border:`2px solid ${p.color}`,textAlign:'center',minWidth:50}}>
                  <div style={{color:p.color,fontSize:10,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:60}}>{p.name}</div>
                  <div style={{color:'#e0e0e0',fontSize:16,fontWeight:700}}>{seriesWins[p.id]||0}</div>
                </div>
              ))}
              <div style={{background:'rgba(0,20,40,0.8)',padding:'6px 12px',borderRadius:4,border:'1px solid #7890a0',display:'flex',alignItems:'center'}}>
                <span style={{color:'#7890a0',fontSize:10}}>FIRST TO {winsNeeded}</span>
              </div>
            </div>
          ) : (
            <div style={{background:'rgba(0,20,40,0.8)',padding:'10px 20px',borderRadius:4,border:'1px solid #ffd700'}}>
              <span style={{color:'#3498db',fontSize:18,fontWeight:700}}>{myWins}</span>
              <span style={{color:'#7890a0',fontSize:14,margin:'0 8px'}}>-</span>
              <span style={{color:'#e74c3c',fontSize:18,fontWeight:700}}>{oppWins}</span>
              <span style={{color:'#7890a0',fontSize:11,marginLeft:8}}>BEST OF {winsNeeded*2-1}</span>
            </div>
          )}
          {opponent&&<div style={{background:'rgba(0,20,40,0.8)',padding:'10px 20px',borderRadius:4,border:'1px solid #ff6b35'}}>
            <span style={{color:'#7890a0',fontSize:12}}>VS </span><span style={{color:'#ff6b35',fontSize:14,fontWeight:700}}>{opponent}</span></div>}
          <div style={{background:overtime?'rgba(255,0,0,0.3)':'rgba(0,20,40,0.8)',padding:'10px 20px',borderRadius:4,border:overtime?'2px solid #ff4444':'1px solid #7890a0',animation:overtime?'pulse 0.5s ease infinite':'none'}}>
            <span style={{color:overtime?'#ff4444':'#7890a0',fontSize:12}}>{overtime?'⚠️ OVERTIME':'TIME'} </span>
            <span style={{color:overtime?'#ff4444':battleTimer<=10?'#ffaa00':'#e0e0e0',fontSize:18,fontWeight:700}}>{overtime?'☠️':battleTimer+'s'}</span></div>
          <div style={{background:'rgba(0,20,40,0.8)',padding:'10px 20px',borderRadius:4,border:'1px solid #ffaa00'}}>
            <span style={{color:'#7890a0',fontSize:12}}>AMMO </span><span style={{color:pBC>=pMB?'#ff4444':'#ffaa00',fontSize:18,fontWeight:700}}>{pBC}/{pMB}</span></div>
        </div>
        <div style={{display:'flex',gap:10}}>
          {(playerUps||[]).map((id)=>(<UpgradeIcon key={id} id={id} />))}
        </div>
      </div>
      <div ref={cvRef} onContextMenu={(e)=>e.preventDefault()} style={{width:AW,height:AH,background:overtime?'linear-gradient(180deg,#200d0d,#301515)':'linear-gradient(180deg,#0d1520,#152030)',border:overtime?'3px solid #ff4444':'3px solid #00d4ff',borderRadius:8,position:'relative',overflow:'hidden',boxShadow:overtime?'0 0 40px rgba(255,0,0,0.3),inset 0 0 100px rgba(40,0,0,0.5)':'0 0 40px rgba(0,212,255,0.3),inset 0 0 100px rgba(0,20,40,0.5)',cursor:'crosshair'}}>
        <svg style={{position:'absolute',width:'100%',height:'100%',opacity:0.1}}><defs><pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse"><path d="M 50 0 L 0 0 0 50" fill="none" stroke={overtime?'#ff4444':'#00d4ff'} strokeWidth="0.5"/></pattern></defs><rect width="100%" height="100%" fill="url(#grid)"/></svg>
        {(obstacles||[]).map((o)=>(<div key={o.id} style={{position:'absolute',left:o.x,top:o.y,width:o.width,height:o.height,background:'linear-gradient(135deg,#2a3a5a,#1a2a40)',border:'2px solid #3a4a6a',borderRadius:4,boxShadow:'0 4px 15px rgba(0,0,0,0.5)'}}/>))}
        {(shields||[]).map((s)=>(<div key={s.id} style={{position:'absolute',left:s.x-25,top:s.y-25,width:50,height:50,borderRadius:'50%',border:'3px solid #00ff88',background:'radial-gradient(circle,rgba(0,255,136,0.3),transparent 70%)',animation:'pulse 1s ease infinite'}}/>))}
        {(mines||[]).map((m)=>{if(m.invisible&&m.ownerId!==myId)return null;const isOT=m.isOvertime||m.ownerId==='overtime';return(<div key={m.id} style={{position:'absolute',left:m.x-MS/2,top:m.y-MS/2,width:isOT?MS*1.5:MS,height:isOT?MS*1.5:MS,borderRadius:'50%',background:isOT?'radial-gradient(circle,#ff00ff,#880088)':'radial-gradient(circle,#ff4444,#aa0000)',border:isOT?'3px solid #ff88ff':'2px solid #ff6666',boxShadow:isOT?'0 0 20px rgba(255,0,255,0.8)':'0 0 10px rgba(255,0,0,0.5)',animation:'pulse 0.5s ease infinite'}}/>);})}
        {(bullets||[]).map((b)=>(<div key={b.id} style={{position:'absolute',left:b.x-(b.size||BS)/2,top:b.y-(b.size||BS)/2,width:b.size||BS,height:b.size||BS,borderRadius:'50%',background:b.piercing?'radial-gradient(circle,#ff44ff,#aa00ff)':'radial-gradient(circle,#ffff00,#ff8800)',boxShadow:b.piercing?'0 0 10px rgba(255,0,255,0.8)':'0 0 10px rgba(255,255,0,0.8)'}}/>))}
        {(tanks||[]).map((t)=>{if(!t.alive)return null;const ghostOp=(t.hasGhost&&t.isMoving)?0.4:1;return(
          <div key={t.id} style={{position:'absolute',left:t.x-TS/2,top:t.y-TS/2,width:TS,height:TS,opacity:ghostOp,transition:'opacity 0.2s'}}>
            <div style={{position:'absolute',width:'100%',height:'100%',transform:'rotate('+t.angle+'rad)',transformOrigin:'center'}}>
              <div style={{position:'absolute',top:0,left:2,width:36,height:8,background:'#333',borderRadius:2}}/><div style={{position:'absolute',bottom:0,left:2,width:36,height:8,background:'#333',borderRadius:2}}/>
              <div style={{position:'absolute',top:6,left:4,width:32,height:28,background:'linear-gradient(135deg,'+t.color+','+t.color+'88)',borderRadius:4,border:'2px solid '+t.color}}/></div>
            <div style={{position:'absolute',width:'100%',height:'100%',transform:'rotate('+t.turretAngle+'rad)',transformOrigin:'center'}}>
              <div style={{position:'absolute',top:'50%',left:'50%',width:16,height:16,marginLeft:-8,marginTop:-8,background:t.color,borderRadius:'50%',border:'2px solid #fff'}}/>
              <div style={{position:'absolute',top:'50%',left:'50%',width:24,height:6,marginTop:-3,background:'linear-gradient(90deg,'+t.color+',#666)',borderRadius:'0 3px 3px 0'}}/></div>
            {t.hasArmor&&(<div style={{position:'absolute',top:-2,left:-2,width:TS+4,height:TS+4,borderRadius:'50%',border:'2px solid #00ffff',boxShadow:'0 0 10px rgba(0,255,255,0.5)',pointerEvents:'none'}}/>)}
            <div style={{position:'absolute',top:-25,left:'50%',transform:'translateX(-50%)',whiteSpace:'nowrap',fontSize:11,fontFamily:"'Rajdhani',sans-serif",fontWeight:600,color:t.color,textShadow:'0 0 5px rgba(0,0,0,0.8)',padding:'2px 6px',background:'rgba(0,0,0,0.5)',borderRadius:3}}>{t.hasArmor?'🛡️ ':''}{t.name}</div></div>);})}
        {(explosions||[]).map((e)=>(<div key={e.id} style={{position:'absolute',left:e.x-e.radius,top:e.y-e.radius,width:e.radius*2,height:e.radius*2,borderRadius:'50%',background:'radial-gradient(circle,rgba(255,200,0,0.9),rgba(255,100,0,0.6) 50%,transparent 70%)',animation:'explosion 0.5s ease-out forwards',pointerEvents:'none'}}/>))}
        {battleWinner&&(<div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',background:'rgba(0,20,40,0.95)',padding:'30px 50px',borderRadius:12,border:'3px solid #00d4ff',textAlign:'center',animation:'slideIn 0.3s ease'}}>
          <div style={{fontSize:48,marginBottom:10}}>🏆</div><div style={{fontSize:24,color:'#00d4ff',fontWeight:700}}>{battleWinner} WINS!</div></div>)}
      </div>
    </div>
  );
}

/* ===== MAIN COMPONENT ===== */
export default function TankiesIO() {
  // phase: menu | online_lobby | online_countdown | online_battle | online_upgrade | online_result | online_series_end
  //      | ffa_lobby | ffa_countdown | ffa_battle | ffa_upgrade | ffa_result | ffa_match_end
  //      | tourn_lobby | tourn_bracket | tourn_upgrade | tourn_countdown | tourn_battle | tourn_result | tourn_victory | tourn_defeat
  const [phase, setPhase] = useState('menu');
  const [playerName, setPlayerName] = useState('');
  const [, forceRender] = useState(0);

  // --- Quick match / FFA wins tracking ---
  const [seriesWins, setSeriesWins] = useState({}); // playerId -> win count
  const [winsNeeded, setWinsNeeded] = useState(3);
  const [seriesWinner, setSeriesWinner] = useState(null);
  const [ffaPlayers, setFfaPlayers] = useState([]); // All FFA players with colors
  const [tournEliminated, setTournEliminated] = useState(false); // Track if player lost in tournament

  // --- (Unused offline state kept for compatibility) ---
  const [tournament, setTournament] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [upgradeChoices, setUpgradeChoices] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [battleWinner, setBattleWinner] = useState(null);
  const worldRef = useRef(null);
  const matchRef = useRef(null);

  // --- Online state ---
  const wsRef = useRef(null);
  const myIdRef = useRef(null);
  const disconnectingRef = useRef(false); // Track when we're intentionally disconnecting
  const onlineStateRef = useRef(null);
  const prevOnlineStateRef = useRef(null);
  const stateTimeRef = useRef(0);
  const renderStateRef = useRef(null);
  const [onlineObstacles, setOnlineObstacles] = useState([]);
  const [onlineRound, setOnlineRound] = useState(1);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [onlineUpgradeChoices, setOnlineUpgradeChoices] = useState([]);
  const [onlineWinner, setOnlineWinner] = useState(null);
  const [lobbyStatus, setLobbyStatus] = useState('');
  const [pingMs, setPingMs] = useState(0);
  const [opponent, setOpponent] = useState('');

  // --- Tournament lobby state ---
  const [tournLobbyPlayers, setTournLobbyPlayers] = useState([]);
  const [tournLobbyTimer, setTournLobbyTimer] = useState(60);
  const [tournBracket, setTournBracket] = useState([]);
  const [tournRoundHistory, setTournRoundHistory] = useState([]);
  const [tournRound, setTournRound] = useState(1);
  const [tournChampion, setTournChampion] = useState(null);
  const [upgradeTimer, setUpgradeTimer] = useState(10);
  const [battleTimer, setBattleTimer] = useState(60);
  const [overtime, setOvertime] = useState(false);
  const [myCurrentUpgrades, setMyCurrentUpgrades] = useState([]); // Track player's upgrades between matches

  // --- Input refs ---
  const keysRef = useRef({});
  const mpRef = useRef({x: AW/2, y: AH/2});
  const mbRef = useRef({left: false, right: false});
  const cvRef = useRef(null);
  const loopRef = useRef(null);

  // ======================== OFFLINE LOGIC ========================
  const getUps = useCallback(() => {
    const p = tournament && tournament.players ? tournament.players.find(p => p.id === 'player') : null;
    return p ? p.upgrades : [];
  }, [tournament]);

  const startSolo = useCallback(() => {
    if (!playerName.trim()) return;
    const t = createTourn(playerName.trim()); t.bracket = simBots(t.bracket);
    setTournament(t); setCurrentRound(1); setPhase('solo_bracket');
  }, [playerName]);

  const launchSoloBattle = useCallback((match) => {
    matchRef.current = match; const obs = genObs(), ais = {};
    const mk = (p,x,a) => { const t = {id:p.id,name:p.name,x,y:AH/2,angle:a,turretAngle:a,isBot:p.isBot,upgrades:[...p.upgrades],color:p.isBot?'#e74c3c':'#3498db',alive:true,difficulty:p.difficulty||0.5,hasArmor:(p.upgrades||[]).includes('tankArmor'),hasGhost:(p.upgrades||[]).includes('ghostTank'),isMoving:false,_lastShot:0,_lastMine:0,_obc:0}; if(p.isBot) ais[p.id]=new BotAI(t.difficulty); return t; };
    worldRef.current = {tanks:[mk(match.player1,80,0),mk(match.player2,AW-80,Math.PI)],bullets:[],mines:[],shields:[],explosions:[],obstacles:obs,botAIs:ais};
    setBattleWinner(null); setCountdown(3); setPhase('solo_countdown');
    let c=3; const iv=setInterval(()=>{c--;setCountdown(c);if(c<=0){clearInterval(iv);setPhase('solo_battle');}},1000);
  }, []);

  const handleSoloEnd = useCallback((winner) => {
    if (!tournament) return; const result = advanceTourn(tournament, winner);
    setTournament(result.tournament); setCurrentRound(result.tournament.round);
    setPhase(winner.id === 'player' ? ('solo_' + result.phase) : 'solo_defeat');
  }, [tournament]);

  const contSoloBracket = useCallback(() => {
    if (!tournament) return; const pm = findPM(tournament.bracket); if (!pm) return;
    matchRef.current = pm; setUpgradeChoices(rndUp(2, tournament.players.find(p=>p.id==='player')?.upgrades || [])); setPhase('solo_upgrade');
  }, [tournament]);

  const selSoloUpgrade = useCallback((up) => {
    if (!tournament) return;
    const upl = tournament.players.map(p => p.id==='player' ? {...p,upgrades:[...(p.upgrades||[]),up.id]} : p);
    const ubr = tournament.bracket.map(m => ({...m,player1:m.player1.id==='player'?{...m.player1,upgrades:[...(m.player1.upgrades||[]),up.id]}:m.player1,player2:m.player2.id==='player'?{...m.player2,upgrades:[...(m.player2.upgrades||[]),up.id]}:m.player2}));
    setTournament({...tournament,players:upl,bracket:ubr}); const pm = findPM(ubr); if (pm) launchSoloBattle(pm);
  }, [tournament, launchSoloBattle]);

  // Solo game loop
  useEffect(() => {
    if (phase !== 'solo_battle') return; let winDone = false;
    const loop = () => { const w = worldRef.current; if (!w) { loopRef.current = requestAnimationFrame(loop); return; }
      const rect = cvRef.current ? cvRef.current.getBoundingClientRect() : null;
      const result = offlineTick(w, {keys:{...keysRef.current},mousePos:{...mpRef.current},mouseButtons:{...mbRef.current},canvasRect:rect,now:Date.now()});
      forceRender(n=>n+1);
      if (result.alive.length<=1 && !winDone) { winDone=true;
        if (result.alive.length===1) { setBattleWinner(result.alive[0].name); setTimeout(()=>handleSoloEnd(result.alive[0]),2000); }
        else { const m=matchRef.current; if(m)setTimeout(()=>launchSoloBattle(m),2000); } }
      loopRef.current = requestAnimationFrame(loop); };
    loopRef.current = requestAnimationFrame(loop);
    return () => { if (loopRef.current) cancelAnimationFrame(loopRef.current); };
  }, [phase, handleSoloEnd, launchSoloBattle]);

  // ======================== WEBSOCKET CONNECTION ========================
  const connectWS = useCallback((mode) => {
    if (!playerName.trim()) return;
    disconnectingRef.current = false; // Reset flag when connecting
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws._mode = mode;

    ws.onopen = () => {
      if (mode === 'quick') {
        ws.send(JSON.stringify({type:'joinQuick', name:playerName.trim()}));
        setLobbyStatus('Searching for opponent...');
        setPhase('online_lobby');
      } else if (mode === 'ffa') {
        ws.send(JSON.stringify({type:'joinFFA', name:playerName.trim()}));
        setLobbyStatus('Waiting for players...');
        setPhase('ffa_lobby');
      } else if (mode === 'tournament') {
        ws.send(JSON.stringify({type:'joinTournament', name:playerName.trim()}));
        setPhase('tourn_lobby');
      }
      const pingIv = setInterval(() => { if (ws.readyState===1) ws.send(JSON.stringify({type:'ping',t:Date.now()})); }, 2000);
      ws._pingIv = pingIv;
    };

    ws.onmessage = (evt) => {
      // Ignore messages if we're intentionally disconnecting
      if (disconnectingRef.current) return;
      
      const msg = JSON.parse(evt.data);
      switch (msg.type) {
        // Quick match
        case 'lobbyJoined': myIdRef.current = msg.id; break;
        case 'lobbyWait': setLobbyStatus('Waiting for opponent... (' + msg.total + ' in lobby)'); break;
        case 'matchStart':
          setOnlinePlayers(msg.players); setOnlineObstacles(msg.obstacles); setOnlineRound(msg.round);
          const opp = msg.players.find(p => p.id !== myIdRef.current);
          setOpponent(opp ? opp.name : '');
          // Track my upgrades from match start
          const myPlayer = msg.players.find(p => p.id === myIdRef.current);
          if (myPlayer) setMyCurrentUpgrades(myPlayer.upgrades || []);
          // Track wins
          if (msg.wins) setSeriesWins(msg.wins);
          if (msg.winsNeeded) setWinsNeeded(msg.winsNeeded);
          setSeriesWinner(null);
          onlineStateRef.current = null; prevOnlineStateRef.current = null; renderStateRef.current = null;
          setOnlineWinner(null); setPhase('online_countdown'); break;
        case 'countdown': setCountdown(msg.value); break;
        case 'battleStart': 
          setBattleTimer(msg.battleTimer || 60);
          setOvertime(false);
          if (ws._mode === 'tournament') {
            setPhase('tourn_battle');
          } else if (ws._mode === 'ffa') {
            setPhase('ffa_battle');
          } else {
            setPhase('online_battle');
          }
          break;
        case 'state': 
          prevOnlineStateRef.current = onlineStateRef.current;
          onlineStateRef.current = msg;
          stateTimeRef.current = Date.now();
          if (msg.battleTimer !== undefined) setBattleTimer(msg.battleTimer);
          if (msg.overtime !== undefined) setOvertime(msg.overtime);
          if (msg.wins) setSeriesWins(msg.wins);
          break;
        case 'overtime':
          setOvertime(true);
          break;
        case 'battleEnd':
          setOnlineWinner(msg.winnerName || 'Draw');
          if (msg.wins) setSeriesWins(msg.wins);
          if (msg.seriesWinnerId) {
            setSeriesWinner({ id: msg.seriesWinnerId, name: msg.seriesWinnerName });
          }
          setPhase('online_result'); 
          break;
        case 'seriesEnd':
          setSeriesWinner({ id: msg.winnerId, name: msg.winnerName });
          if (msg.wins) setSeriesWins(msg.wins);
          setPhase('online_series_end');
          break;
        case 'upgradeChoices':
          setOnlineUpgradeChoices(msg.choices); 
          setUpgradeTimer(msg.timer || 10);
          if (msg.wins) setSeriesWins(msg.wins);
          if (ws._mode === 'ffa') {
            setPhase('ffa_upgrade');
          } else {
            setPhase('online_upgrade');
          }
          break;
        case 'upgradeTimer':
          setUpgradeTimer(msg.timer);
          break;
        
        // FFA (Free For All)
        case 'ffaLobbyJoined': myIdRef.current = msg.id; break;
        case 'ffaLobbyWait': 
          setLobbyStatus(`Waiting for players... (${msg.total}/${msg.needed})`); 
          break;
        case 'ffaMatchStart':
          setFfaPlayers(msg.players);
          setOnlineObstacles(msg.obstacles);
          setOnlineRound(msg.round);
          if (msg.wins) setSeriesWins(msg.wins);
          if (msg.winsNeeded) setWinsNeeded(msg.winsNeeded);
          const myFfaPlayer = msg.players.find(p => p.id === myIdRef.current);
          if (myFfaPlayer) setMyCurrentUpgrades(myFfaPlayer.upgrades || []);
          onlineStateRef.current = null; prevOnlineStateRef.current = null; renderStateRef.current = null;
          setOnlineWinner(null);
          setSeriesWinner(null);
          setPhase('ffa_countdown');
          break;
        case 'ffaBattleEnd':
          setOnlineWinner(msg.winnerName || 'Draw');
          if (msg.wins) setSeriesWins(msg.wins);
          if (msg.matchWinnerId) {
            setSeriesWinner({ id: msg.matchWinnerId, name: msg.matchWinnerName });
          }
          setPhase('ffa_result');
          break;
        case 'ffaUpgradeChoices':
          setOnlineUpgradeChoices(msg.choices);
          setUpgradeTimer(msg.timer || 10);
          if (msg.wins) setSeriesWins(msg.wins);
          if (msg.winsNeeded) setWinsNeeded(msg.winsNeeded);
          setOnlineRound(msg.round || onlineRound);
          setPhase('ffa_upgrade');
          break;
        case 'ffaMatchEnd':
          setSeriesWinner({ id: msg.winnerId, name: msg.winnerName });
          if (msg.wins) setSeriesWins(msg.wins);
          setPhase('ffa_match_end');
          break;

        // Tournament
        case 'tournamentJoined': 
          myIdRef.current = msg.id; 
          setTournEliminated(false); // Reset elimination state
          break;
        case 'tournamentLobby':
          setTournLobbyPlayers(msg.players); setTournLobbyTimer(msg.timer); break;
        case 'tournamentBracket':
          setTournBracket(msg.bracket); setTournRoundHistory(msg.roundHistory || []);
          setTournRound(msg.round); setTournChampion(msg.champion);
          if (msg.champion) {
            setPhase(msg.champion.id === myIdRef.current ? 'tourn_victory' : 'tourn_defeat');
          } else if (phase === 'tourn_lobby') {
            setPhase('tourn_bracket');
          }
          break;
        case 'tournamentUpgradeChoice':
          setOnlineUpgradeChoices(msg.choices); setOpponent(msg.opponent); setTournRound(msg.round);
          setUpgradeTimer(msg.timer || 10);
          setPhase('tourn_upgrade'); break;
        case 'tournamentMatchStart':
          setOnlineObstacles(msg.obstacles); setTournRound(msg.round);
          const p1 = msg.player1, p2 = msg.player2;
          setOpponent(p1.id === myIdRef.current ? p2.name : p1.name);
          setOnlinePlayers([p1, p2]);
          // Track my upgrades from match start
          const myTournPlayer = p1.id === myIdRef.current ? p1 : p2;
          setMyCurrentUpgrades(myTournPlayer.upgrades || []);
          onlineStateRef.current = null; prevOnlineStateRef.current = null; renderStateRef.current = null;
          setOnlineWinner(null); setPhase('tourn_countdown'); break;
        case 'tournamentMatchEnd':
          setOnlineWinner(msg.winnerName);
          // Check if player lost
          if (msg.winnerName !== playerName) {
            setTournEliminated(true);
          }
          setPhase('tourn_result');
          // Auto-transition to bracket view after 2 seconds (only if still connected)
          setTimeout(() => {
            if (wsRef.current && wsRef.current.readyState === 1) {
              setPhase('tourn_bracket');
            }
          }, 2000);
          break;
        
        case 'pong': setPingMs(Date.now() - msg.t); break;
      }
    };

    ws.onclose = () => {
      clearInterval(ws._pingIv);
      if (phase.startsWith('online') || phase.startsWith('tourn') || phase.startsWith('ffa')) setPhase('menu');
    };
    ws.onerror = () => { setLobbyStatus('Connection failed. Is the server running?'); };
  }, [playerName, phase]);

  // Online: send input to server
  useEffect(() => {
    if (phase !== 'online_battle' && phase !== 'tourn_battle' && phase !== 'ffa_battle') return;
    const iv = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) return;
      const rect = cvRef.current ? cvRef.current.getBoundingClientRect() : null;
      let turretAngle = 0;
      const st = renderStateRef.current || onlineStateRef.current;
      if (st && rect) {
        const me = st.tanks.find(t => t.id === myIdRef.current);
        if (me) {
          const sx = AW/rect.width, sy = AH/rect.height;
          turretAngle = Math.atan2(mpRef.current.y*sy - me.y, mpRef.current.x*sx - me.x);
        }
      }
      ws.send(JSON.stringify({type:'input',input:{keys:{...keysRef.current},mousePos:{turretAngle},mouseButtons:{...mbRef.current}}}));
    }, 1000/30);
    return () => clearInterval(iv);
  }, [phase]);

  // Online: render loop with interpolation
  useEffect(() => {
    if (phase !== 'online_battle' && phase !== 'tourn_battle' && phase !== 'ffa_battle') return;
    const INTERP_DELAY = 50;
    const lerp = (a, b, t) => a + (b - a) * Math.min(1, Math.max(0, t));
    const lerpAngle = (a, b, t) => { let diff = b - a; while (diff > Math.PI) diff -= 2 * Math.PI; while (diff < -Math.PI) diff += 2 * Math.PI; return a + diff * Math.min(1, Math.max(0, t)); };
    
    const renderLoop = () => {
      const now = Date.now();
      const curr = onlineStateRef.current, prev = prevOnlineStateRef.current;
      if (curr) {
        if (prev && stateTimeRef.current > 0) {
          const t = Math.min(1, (now - stateTimeRef.current) / INTERP_DELAY);
          const interpTanks = curr.tanks.map(ct => {
            const pt = prev.tanks.find(p => p.id === ct.id);
            if (!pt) return ct;
            return {...ct, x: lerp(pt.x, ct.x, t), y: lerp(pt.y, ct.y, t), angle: lerpAngle(pt.angle, ct.angle, t), turretAngle: lerpAngle(pt.turretAngle, ct.turretAngle, t)};
          });
          renderStateRef.current = {...curr, tanks: interpTanks};
        } else {
          renderStateRef.current = curr;
        }
        forceRender(n => n + 1);
      }
      loopRef.current = requestAnimationFrame(renderLoop);
    };
    loopRef.current = requestAnimationFrame(renderLoop);
    return () => { if (loopRef.current) cancelAnimationFrame(loopRef.current); };
  }, [phase]);

  const selOnlineUpgrade = useCallback((up) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({type:'selectUpgrade', upgradeId:up.id}));
    // Track the newly selected upgrade
    setMyCurrentUpgrades(prev => [...prev, up.id]);
    if (phase === 'tourn_upgrade') {
      setPhase('tourn_bracket');
      setLobbyStatus('Waiting for opponent...');
    } else {
      setPhase('online_lobby');
      setLobbyStatus('Waiting for opponent to choose...');
    }
  }, [phase]);

  const disconnectOnline = useCallback(() => {
    disconnectingRef.current = true; // Set flag to ignore incoming messages
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setTournEliminated(false);
    setPhase('menu');
  }, []);

  // ======================== INPUT LISTENERS ========================
  useEffect(() => {
    const kd=(e)=>{if(e.target.tagName==='INPUT'||e.target.tagName==='TEXTAREA')return;keysRef.current[e.key.toLowerCase()]=true;};
    const ku=(e)=>{if(e.target.tagName==='INPUT'||e.target.tagName==='TEXTAREA')return;keysRef.current[e.key.toLowerCase()]=false;};
    const mm=(e)=>{if(cvRef.current){const r=cvRef.current.getBoundingClientRect();mpRef.current={x:e.clientX-r.left,y:e.clientY-r.top};}};
    const md=(e)=>{
      if(e.target.tagName==='INPUT'||e.target.tagName==='TEXTAREA'||e.target.tagName==='BUTTON'||e.target.closest('.ucard'))return;
      if(cvRef.current&&cvRef.current.contains(e.target)){e.preventDefault();}
      if(e.button===0)mbRef.current.left=true;if(e.button===2)mbRef.current.right=true;
    };
    const mu=(e)=>{if(e.button===0)mbRef.current.left=false;if(e.button===2)mbRef.current.right=false;};
    const cm=(e)=>{if(cvRef.current&&cvRef.current.contains(e.target))e.preventDefault();};
    window.addEventListener('keydown',kd);window.addEventListener('keyup',ku);window.addEventListener('mousemove',mm);
    window.addEventListener('mousedown',md);window.addEventListener('mouseup',mu);window.addEventListener('contextmenu',cm);
    return()=>{window.removeEventListener('keydown',kd);window.removeEventListener('keyup',ku);window.removeEventListener('mousemove',mm);
      window.removeEventListener('mousedown',md);window.removeEventListener('mouseup',mu);window.removeEventListener('contextmenu',cm);};
  }, []);

  // ======================== RENDER DATA ========================
  const ow = worldRef.current;
  const oTanks = ow ? ow.tanks : [], oBullets = ow ? ow.bullets : [], oMines = ow ? ow.mines : [];
  const oShields = ow ? ow.shields : [], oExplosions = ow ? ow.explosions : [], oObs = ow ? ow.obstacles : [];
  const oPBC = oBullets.filter(b=>b.ownerId==='player').length;
  const oPMB = getUps().includes('extraBullets') ? 8 : MAXB;
  
  const ns = renderStateRef.current || onlineStateRef.current;
  const nTanks = ns ? ns.tanks : [], nBullets = ns ? ns.bullets : [], nMines = ns ? ns.mines : [];
  const nShields = ns ? ns.shields : [], nExplosions = ns ? ns.explosions : [];
  const nPBC = nBullets.filter(b=>b.ownerId===myIdRef.current).length;
  const myOnlineTank = ns ? ns.tanks.find(t=>t.id===myIdRef.current) : null;
  const myOnlineUps = myOnlineTank ? myOnlineTank.upgrades : [];
  const nPMB = myOnlineUps.includes('extraBullets') ? 8 : MAXB;

  const upBadge = (id) => { const u=UPGRADES.find(x=>x.id===id); return u ? (<span key={id} style={{background:'rgba(0,255,136,0.2)',padding:'5px 10px',borderRadius:4,fontSize:14,border:'1px solid #00ff88'}}>{u.icon} {u.name}</span>) : null; };

  // ======================== JSX ========================
  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#1a1a2e,#16213e 50%,#0f3460)',fontFamily:"'Orbitron','Rajdhani',sans-serif",color:'#e0e0e0',overflow:'hidden'}}>
      <style>{CSS}</style>

      {/* MENU */}
      {phase === 'menu' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:40,padding:20}}>
          <div style={{textAlign:'center',animation:'slideIn 0.5s ease'}}>
            <h1 style={{fontSize:'clamp(48px,10vw,80px)',fontWeight:900,margin:0,background:'linear-gradient(135deg,#00d4ff,#00ff88 50%,#ff00aa)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',letterSpacing:4}}>TANKEEZ</h1>
            <p style={{fontSize:18,color:'#7890a0',marginTop:10,fontFamily:"'Rajdhani',sans-serif",letterSpacing:3}}>ONLINE TANK BATTLES</p>
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:20,animation:'slideIn 0.5s ease 0.2s both'}}>
            <input type="text" placeholder="Enter your callsign..." value={playerName} onChange={(e)=>setPlayerName(e.target.value)} maxLength={16} onKeyDown={(e)=>{if(e.key==='Enter'&&playerName.trim())connectWS('quick');}} />
            <div style={{display:'flex',gap:15,flexWrap:'wrap',justifyContent:'center'}}>
              <button className="btn btn-alt" onClick={()=>connectWS('quick')} disabled={!playerName.trim()}>1v1 (Bo5)</button>
              <button className="btn" onClick={()=>connectWS('ffa')} disabled={!playerName.trim()}>4-Player FFA</button>
              <button className="btn btn-green" onClick={()=>connectWS('tournament')} disabled={!playerName.trim()}>16-Player Tournament</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:5,marginTop:5}}>
              <p style={{color:'#7890a0',fontSize:12,margin:0}}>1v1: Best of 5 • FFA: First to 3 wins • Tournament: 16 players, single elimination</p>
            </div>
          </div>
          <div style={{display:'flex',gap:60,marginTop:20,animation:'slideIn 0.5s ease 0.4s both'}}>
            {[['🎮','WASD','Move'],['🎯','Mouse','Aim'],['💥','LMB','Shoot'],['💣','RMB','Mine']].map(([ic,k,a])=>(
              <div key={k} style={{textAlign:'center'}}><div style={{fontSize:32,marginBottom:8}}>{ic}</div><div style={{fontSize:14,color:'#7890a0',fontFamily:"'Rajdhani',sans-serif"}}><strong style={{color:'#00d4ff'}}>{k}</strong> - {a}</div></div>))}
          </div>
        </div>
      )}

      {/* TOURNAMENT LOBBY */}
      {phase === 'tourn_lobby' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:48,marginBottom:10}}>🏆</div>
          <h2 style={{fontSize:28,fontWeight:700,color:'#00ff88',margin:0}}>TOURNAMENT LOBBY</h2>
          <div className="spinner" />
          <p style={{color:'#7890a0',fontSize:16}}>Waiting for players... ({tournLobbyPlayers.length}/16)</p>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center',maxWidth:800}}>
            {tournLobbyPlayers.map((p,i) => (
              <div key={i} style={{background:p.id===myIdRef.current?'rgba(0,255,136,0.2)':'rgba(0,20,40,0.8)',padding:'8px 14px',borderRadius:4,border:p.id===myIdRef.current?'2px solid #00ff88':'1px solid #3a4a6a',fontSize:14}}>
                {p.id===myIdRef.current ? '👤 ' : ''}{p.name}
              </div>
            ))}
            {Array(16 - tournLobbyPlayers.length).fill(0).map((_,i) => (
              <div key={'empty'+i} style={{background:'rgba(0,20,40,0.4)',padding:'8px 14px',borderRadius:4,border:'1px dashed #3a4a6a',color:'#3a4a6a',fontSize:14}}>
                ...
              </div>
            ))}
          </div>
          {pingMs > 0 && (<p style={{color:'#3a4a6a',fontSize:12}}>Ping: {pingMs}ms</p>)}
          <button className="btn" onClick={disconnectOnline} style={{marginTop:20}}>Leave</button>
        </div>
      )}

      {/* FFA LOBBY */}
      {phase === 'ffa_lobby' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:48,marginBottom:10}}>⚔️</div>
          <h2 style={{fontSize:28,fontWeight:700,color:'#ff6b35',margin:0}}>FREE FOR ALL</h2>
          <div className="spinner" />
          <p style={{color:'#7890a0',fontSize:16}}>{lobbyStatus}</p>
          {pingMs > 0 && (<p style={{color:'#3a4a6a',fontSize:14}}>Ping: {pingMs}ms</p>)}
          <button className="btn" onClick={disconnectOnline} style={{marginTop:20}}>Cancel</button>
        </div>
      )}

      {/* FFA COUNTDOWN */}
      {phase === 'ffa_countdown' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
          <div style={{fontSize:150,fontWeight:900,color:'#ff6b35',textShadow:'0 0 60px rgba(255,107,53,0.8)',animation:'countdown 1s ease infinite'}}>{countdown}</div>
          <p style={{fontSize:24,color:'#7890a0',marginTop:20}}>4-Player Free For All</p>
          <div style={{display:'flex',gap:15,marginTop:20}}>
            {ffaPlayers.map((p, i) => (
              <div key={p.id} style={{padding:'8px 15px',borderRadius:4,background:'rgba(0,20,40,0.8)',border:`2px solid ${p.color}`,color:p.color,fontWeight:600}}>
                {p.id === myIdRef.current ? '👤 ' : ''}{p.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FFA BATTLE */}
      {phase === 'ffa_battle' && (
        <Arena tanks={nTanks} bullets={nBullets} mines={nMines} shields={nShields} explosions={nExplosions} obstacles={onlineObstacles}
          cvRef={cvRef} battleWinner={null} myId={myIdRef.current} playerUps={myOnlineUps} currentRound={onlineRound} pBC={nPBC} pMB={nPMB} opponent={null} battleTimer={battleTimer} overtime={overtime}
          seriesWins={seriesWins} winsNeeded={winsNeeded} onlinePlayers={ffaPlayers} isTournament={false} isFFA={true} />
      )}

      {/* FFA RESULT */}
      {phase === 'ffa_result' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:80,marginBottom:10}}>{onlineWinner === playerName ? '🎯' : '💥'}</div>
          <h1 style={{fontSize:48,fontWeight:900,color:onlineWinner===playerName?'#00ff88':'#e74c3c',margin:0}}>
            {onlineWinner === playerName ? 'ROUND WON!' : (onlineWinner === 'Draw' ? 'DRAW' : 'ELIMINATED')}
          </h1>
          <p style={{color:'#7890a0',fontSize:18}}>{onlineWinner} wins the round</p>
          <div style={{display:'flex',gap:20,marginTop:10}}>
            {ffaPlayers.map(p => (
              <div key={p.id} style={{textAlign:'center',padding:'10px 20px',borderRadius:8,background:'rgba(0,20,40,0.8)',border:`2px solid ${p.color}`}}>
                <div style={{color:p.color,fontWeight:700,fontSize:14}}>{p.name}</div>
                <div style={{color:'#e0e0e0',fontSize:24,fontWeight:900}}>{seriesWins[p.id] || 0}</div>
              </div>
            ))}
          </div>
          <p style={{color:'#7890a0',fontSize:14}}>First to {winsNeeded} wins</p>
          {seriesWinner ? (
            <p style={{color:'#ffd700',fontSize:18,fontWeight:700}}>
              {seriesWinner.id === myIdRef.current ? '🏆 YOU WIN!' : `${seriesWinner.name} wins!`}
            </p>
          ) : (
            <p style={{color:'#3a4a6a',fontSize:14}}>{onlineWinner === playerName ? 'Upgrade selection coming up...' : 'Next round starting...'}</p>
          )}
        </div>
      )}

      {/* FFA UPGRADE (all players get upgrade each round) */}
      {phase === 'ffa_upgrade' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20}}>
          <div style={{textAlign:'center',animation:'slideIn 0.5s ease'}}>
            <div style={{fontSize:48,marginBottom:10}}>⚔️</div>
            <h2 style={{fontSize:36,fontWeight:700,color:'#ff6b35',margin:0,letterSpacing:3}}>ROUND {onlineRound + 1}</h2>
            <p style={{color:'#7890a0',marginTop:10,fontSize:16}}>Choose your upgrade</p>
            <div style={{marginTop:15,padding:'10px 30px',background:upgradeTimer<=3?'rgba(255,0,0,0.3)':'rgba(0,20,40,0.8)',border:upgradeTimer<=3?'2px solid #ff4444':'2px solid #00d4ff',borderRadius:8,display:'inline-block'}}>
              <span style={{color:'#7890a0',fontSize:14}}>SELECT IN </span>
              <span style={{color:upgradeTimer<=3?'#ff4444':'#00d4ff',fontSize:28,fontWeight:900}}>{upgradeTimer}s</span>
            </div>
          </div>
          {myCurrentUpgrades.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10,animation:'slideIn 0.5s ease 0.1s both'}}>
              <span style={{color:'#7890a0',fontSize:14}}>YOUR UPGRADES:</span>
              <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center'}}>
                {myCurrentUpgrades.map(id => {
                  const u = UPGRADES.find(x => x.id === id);
                  return u ? (
                    <div key={id} style={{background:'rgba(0,255,136,0.15)',padding:'8px 15px',borderRadius:6,border:'1px solid #00ff88',display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:18}}>{u.icon}</span>
                      <span style={{color:'#00ff88',fontSize:13,fontWeight:600}}>{u.name}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
          <div style={{display:'flex',gap:30,flexWrap:'wrap',justifyContent:'center'}}>
            {onlineUpgradeChoices.map((u,i)=>(<div key={u.id} className="ucard" onClick={()=>selOnlineUpgrade(u)} style={{animation:'slideIn 0.5s ease '+(0.1*i+0.2)+'s both'}}>
              <div style={{fontSize:48,marginBottom:15,textAlign:'center'}}>{u.icon}</div>
              <h3 style={{fontSize:20,fontWeight:700,color:'#00d4ff',margin:'0 0 10px',textAlign:'center'}}>{u.name}</h3>
              <p style={{fontSize:14,color:'#7890a0',margin:0,textAlign:'center',fontFamily:"'Rajdhani',sans-serif"}}>{u.desc}</p></div>))}
          </div>
        </div>
      )}

      {/* FFA MATCH END */}
      {phase === 'ffa_match_end' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:100,marginBottom:10}}>{seriesWinner?.id === myIdRef.current ? '🏆' : '😢'}</div>
          <h1 style={{fontSize:56,fontWeight:900,color:seriesWinner?.id===myIdRef.current?'#ffd700':'#e74c3c',margin:0}}>
            {seriesWinner?.id === myIdRef.current ? 'VICTORY!' : 'GAME OVER'}
          </h1>
          <p style={{color:'#7890a0',fontSize:24}}>{seriesWinner?.name} wins!</p>
          <div style={{display:'flex',gap:20,marginTop:10}}>
            {ffaPlayers.map(p => (
              <div key={p.id} style={{textAlign:'center',padding:'15px 25px',borderRadius:8,background:p.id===seriesWinner?.id?'rgba(255,215,0,0.2)':'rgba(0,20,40,0.8)',border:`2px solid ${p.id===seriesWinner?.id?'#ffd700':p.color}`}}>
                <div style={{color:p.id===seriesWinner?.id?'#ffd700':p.color,fontWeight:700,fontSize:16}}>{p.name}</div>
                <div style={{color:'#e0e0e0',fontSize:32,fontWeight:900}}>{seriesWins[p.id] || 0}</div>
              </div>
            ))}
          </div>
          <button className="btn" onClick={disconnectOnline} style={{marginTop:20}}>Play Again</button>
        </div>
      )}

      {/* TOURNAMENT BRACKET */}
      {phase === 'tourn_bracket' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20}}>
          <BracketDisplay bracket={tournBracket} roundHistory={tournRoundHistory} round={tournRound} champion={tournChampion} playerName={playerName} title="ONLINE TOURNAMENT" />
          {tournEliminated ? (
            <>
              <p style={{color:'#e74c3c',fontSize:16,fontWeight:600}}>💀 You have been eliminated</p>
              <p style={{color:'#7890a0',fontSize:14}}>Spectating remaining matches...</p>
              <button className="btn" onClick={disconnectOnline} style={{marginTop:10}}>Leave Tournament</button>
            </>
          ) : (
            <>
              <p style={{color:'#7890a0',fontSize:14}}>Waiting for matches to complete...</p>
              <div className="spinner" />
            </>
          )}
        </div>
      )}

      {/* TOURNAMENT UPGRADE */}
      {phase === 'tourn_upgrade' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20}}>
          <div style={{textAlign:'center',animation:'slideIn 0.5s ease'}}>
            <div style={{fontSize:48,marginBottom:10}}>⚔️</div>
            <h2 style={{fontSize:36,fontWeight:700,color:'#00ff88',margin:0,letterSpacing:3}}>ROUND {tournRound}</h2>
            <p style={{color:'#7890a0',marginTop:10,fontSize:16}}>Next opponent: <strong style={{color:'#ff6b35'}}>{opponent}</strong></p>
            <div style={{marginTop:15,padding:'10px 30px',background:upgradeTimer<=3?'rgba(255,0,0,0.3)':'rgba(0,20,40,0.8)',border:upgradeTimer<=3?'2px solid #ff4444':'2px solid #00d4ff',borderRadius:8,display:'inline-block'}}>
              <span style={{color:'#7890a0',fontSize:14}}>SELECT IN </span>
              <span style={{color:upgradeTimer<=3?'#ff4444':'#00d4ff',fontSize:28,fontWeight:900}}>{upgradeTimer}s</span>
            </div>
          </div>
          {myCurrentUpgrades.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10,animation:'slideIn 0.5s ease 0.1s both'}}>
              <span style={{color:'#7890a0',fontSize:14}}>YOUR UPGRADES:</span>
              <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center'}}>
                {myCurrentUpgrades.map(id => {
                  const u = UPGRADES.find(x => x.id === id);
                  return u ? (
                    <div key={id} style={{background:'rgba(0,255,136,0.15)',padding:'8px 15px',borderRadius:6,border:'1px solid #00ff88',display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:18}}>{u.icon}</span>
                      <span style={{color:'#00ff88',fontSize:13,fontWeight:600}}>{u.name}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
          <div style={{display:'flex',gap:30,flexWrap:'wrap',justifyContent:'center'}}>
            {onlineUpgradeChoices.map((u,i)=>(<div key={u.id} className="ucard" onClick={()=>selOnlineUpgrade(u)} style={{animation:'slideIn 0.5s ease '+(0.1*i+0.2)+'s both'}}>
              <div style={{fontSize:48,marginBottom:15,textAlign:'center'}}>{u.icon}</div>
              <h3 style={{fontSize:20,fontWeight:700,color:'#00d4ff',margin:'0 0 10px',textAlign:'center'}}>{u.name}</h3>
              <p style={{fontSize:14,color:'#7890a0',margin:0,textAlign:'center',fontFamily:"'Rajdhani',sans-serif"}}>{u.desc}</p></div>))}
          </div>
        </div>
      )}

      {/* TOURNAMENT COUNTDOWN */}
      {(phase === 'tourn_countdown' || phase === 'online_countdown') && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
          <div style={{fontSize:150,fontWeight:900,color:'#00d4ff',textShadow:'0 0 60px rgba(0,212,255,0.8)',animation:'countdown 1s ease infinite'}}>{countdown}</div>
          <p style={{fontSize:24,color:'#7890a0',marginTop:20}}>Round {phase === 'tourn_countdown' ? tournRound : onlineRound} — vs {opponent}</p>
        </div>
      )}

      {/* TOURNAMENT/ONLINE BATTLE */}
      {(phase === 'tourn_battle' || phase === 'online_battle') && (
        <Arena tanks={nTanks} bullets={nBullets} mines={nMines} shields={nShields} explosions={nExplosions} obstacles={onlineObstacles}
          cvRef={cvRef} battleWinner={null} myId={myIdRef.current} playerUps={myOnlineUps} currentRound={phase==='tourn_battle'?tournRound:onlineRound} pBC={nPBC} pMB={nPMB} opponent={opponent} battleTimer={battleTimer} overtime={overtime} 
          seriesWins={seriesWins} winsNeeded={winsNeeded} onlinePlayers={onlinePlayers} isTournament={phase==='tourn_battle'} />
      )}

      {/* ONLINE RESULT (quick match round end) */}
      {phase === 'online_result' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:80,marginBottom:10}}>{onlineWinner === playerName ? '🎯' : '💥'}</div>
          <h1 style={{fontSize:48,fontWeight:900,color:onlineWinner===playerName?'#00ff88':'#e74c3c',margin:0}}>
            {onlineWinner === playerName ? 'ROUND WON!' : 'ROUND LOST'}
          </h1>
          <div style={{display:'flex',gap:20,alignItems:'center',marginTop:10}}>
            <span style={{color:'#3498db',fontSize:36,fontWeight:700}}>{seriesWins[myIdRef.current] || 0}</span>
            <span style={{color:'#7890a0',fontSize:24}}>-</span>
            <span style={{color:'#e74c3c',fontSize:36,fontWeight:700}}>{onlinePlayers.find(p=>p.id!==myIdRef.current) ? (seriesWins[onlinePlayers.find(p=>p.id!==myIdRef.current).id] || 0) : 0}</span>
          </div>
          <p style={{color:'#7890a0',fontSize:14}}>First to {winsNeeded} wins</p>
          {seriesWinner ? (
            <p style={{color:'#ffd700',fontSize:18,fontWeight:700}}>
              {seriesWinner.id === myIdRef.current ? '🏆 YOU WIN THE SERIES!' : `${seriesWinner.name} wins the series`}
            </p>
          ) : (
            <p style={{color:'#3a4a6a',fontSize:14}}>Upgrade selection coming up...</p>
          )}
        </div>
      )}

      {/* ONLINE SERIES END */}
      {phase === 'online_series_end' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:100,marginBottom:10}}>{seriesWinner?.id === myIdRef.current ? '🏆' : '😢'}</div>
          <h1 style={{fontSize:56,fontWeight:900,color:seriesWinner?.id===myIdRef.current?'#ffd700':'#e74c3c',margin:0}}>
            {seriesWinner?.id === myIdRef.current ? 'SERIES VICTORY!' : 'SERIES DEFEAT'}
          </h1>
          <div style={{display:'flex',gap:20,alignItems:'center',marginTop:10}}>
            <span style={{color:'#3498db',fontSize:48,fontWeight:700}}>{seriesWins[myIdRef.current] || 0}</span>
            <span style={{color:'#7890a0',fontSize:32}}>-</span>
            <span style={{color:'#e74c3c',fontSize:48,fontWeight:700}}>{onlinePlayers.find(p=>p.id!==myIdRef.current) ? (seriesWins[onlinePlayers.find(p=>p.id!==myIdRef.current).id] || 0) : 0}</span>
          </div>
          <p style={{color:'#7890a0',fontSize:18}}>{seriesWinner?.name} wins!</p>
          <button className="btn" onClick={disconnectOnline} style={{marginTop:20}}>Play Again</button>
        </div>
      )}

      {/* TOURNAMENT RESULT */}
      {phase === 'tourn_result' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:80,marginBottom:10}}>{onlineWinner === playerName ? '🏆' : '💥'}</div>
          <h1 style={{fontSize:48,fontWeight:900,color:onlineWinner===playerName?'#ffd700':'#e74c3c',margin:0}}>
            {onlineWinner === playerName ? 'VICTORY!' : 'DEFEATED'}
          </h1>
          <p style={{color:'#7890a0',fontSize:18}}>{onlineWinner} wins</p>
          {onlineWinner === playerName ? (
            <p style={{color:'#3a4a6a',fontSize:14}}>Advancing to next round...</p>
          ) : (
            <>
              <p style={{color:'#3a4a6a',fontSize:14}}>You can spectate or leave the tournament</p>
              <button className="btn" onClick={disconnectOnline} style={{marginTop:10}}>Leave Tournament</button>
            </>
          )}
        </div>
      )}

      {/* TOURNAMENT VICTORY/DEFEAT */}
      {phase === 'tourn_victory' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20,textAlign:'center'}}>
          <div style={{animation:'slideIn 0.5s ease'}}><div style={{fontSize:80,marginBottom:20}}>🏆</div>
            <h1 style={{fontSize:48,fontWeight:900,background:'linear-gradient(135deg,#ffd700,#ffaa00)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',margin:0}}>TOURNAMENT CHAMPION!</h1>
            <p style={{color:'#7890a0',marginTop:15,fontSize:18}}>Congratulations, {playerName}!</p></div>
          <BracketDisplay bracket={tournBracket} roundHistory={tournRoundHistory} round={tournRound} champion={tournChampion} playerName={playerName} title="FINAL RESULTS" />
          <button className="btn" onClick={disconnectOnline}>Play Again</button>
        </div>
      )}

      {phase === 'tourn_defeat' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20,textAlign:'center'}}>
          <div style={{animation:'slideIn 0.5s ease'}}><div style={{fontSize:80,marginBottom:20}}>💥</div>
            <h1 style={{fontSize:48,fontWeight:900,color:'#e74c3c',margin:0}}>ELIMINATED</h1>
            <p style={{color:'#7890a0',marginTop:15,fontSize:18}}>{tournChampion?.name} won the tournament</p></div>
          <BracketDisplay bracket={tournBracket} roundHistory={tournRoundHistory} round={tournRound} champion={tournChampion} playerName={playerName} title="FINAL RESULTS" />
          <button className="btn" onClick={disconnectOnline}>Play Again</button>
        </div>
      )}

      {/* QUICK MATCH LOBBY */}
      {phase === 'online_lobby' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30}}>
          <div style={{fontSize:48,marginBottom:10}}>🌐</div>
          <h2 style={{fontSize:28,fontWeight:700,color:'#00d4ff',margin:0}}>QUICK MATCH</h2>
          <div className="spinner" />
          <p style={{color:'#7890a0',fontSize:16}}>{lobbyStatus}</p>
          {pingMs > 0 && (<p style={{color:'#3a4a6a',fontSize:14}}>Ping: {pingMs}ms</p>)}
          <button className="btn" onClick={disconnectOnline} style={{marginTop:20}}>Cancel</button>
        </div>
      )}

      {/* ONLINE UPGRADE */}
      {phase === 'online_upgrade' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20}}>
          <div style={{textAlign:'center',animation:'slideIn 0.5s ease'}}>
            <div style={{fontSize:48,marginBottom:10}}>⚔️</div>
            <h2 style={{fontSize:36,fontWeight:700,color:'#00d4ff',margin:0,letterSpacing:3}}>CHOOSE UPGRADE</h2>
            <div style={{marginTop:15,padding:'10px 30px',background:upgradeTimer<=3?'rgba(255,0,0,0.3)':'rgba(0,20,40,0.8)',border:upgradeTimer<=3?'2px solid #ff4444':'2px solid #00d4ff',borderRadius:8,display:'inline-block'}}>
              <span style={{color:'#7890a0',fontSize:14}}>SELECT IN </span>
              <span style={{color:upgradeTimer<=3?'#ff4444':'#00d4ff',fontSize:28,fontWeight:900}}>{upgradeTimer}s</span>
            </div>
          </div>
          {myCurrentUpgrades.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10,animation:'slideIn 0.5s ease 0.1s both'}}>
              <span style={{color:'#7890a0',fontSize:14}}>YOUR UPGRADES:</span>
              <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center'}}>
                {myCurrentUpgrades.map(id => {
                  const u = UPGRADES.find(x => x.id === id);
                  return u ? (
                    <div key={id} style={{background:'rgba(0,255,136,0.15)',padding:'8px 15px',borderRadius:6,border:'1px solid #00ff88',display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:18}}>{u.icon}</span>
                      <span style={{color:'#00ff88',fontSize:13,fontWeight:600}}>{u.name}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
          <div style={{display:'flex',gap:30,flexWrap:'wrap',justifyContent:'center'}}>
            {onlineUpgradeChoices.map((u,i)=>(<div key={u.id} className="ucard" onClick={()=>selOnlineUpgrade(u)} style={{animation:'slideIn 0.5s ease '+(0.1*i+0.2)+'s both'}}>
              <div style={{fontSize:48,marginBottom:15,textAlign:'center'}}>{u.icon}</div>
              <h3 style={{fontSize:20,fontWeight:700,color:'#00d4ff',margin:'0 0 10px',textAlign:'center'}}>{u.name}</h3>
              <p style={{fontSize:14,color:'#7890a0',margin:0,textAlign:'center',fontFamily:"'Rajdhani',sans-serif"}}>{u.desc}</p></div>))}
          </div>
        </div>
      )}

      {/* SOLO BRACKET */}
      {phase === 'solo_bracket' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20}}>
          <BracketDisplay bracket={tournament?.bracket} roundHistory={tournament?.roundHistory} round={currentRound} champion={tournament?.champion} playerName={playerName} />
          {getUps().length>0&&(<div style={{display:'flex',gap:10,alignItems:'center',marginTop:10,flexWrap:'wrap',justifyContent:'center'}}><span style={{color:'#7890a0',fontSize:14}}>Your Upgrades:</span>{getUps().map(upBadge)}</div>)}
          <button className="btn" onClick={contSoloBracket} style={{marginTop:20}}>Start Next Round</button>
        </div>
      )}

      {/* SOLO UPGRADE */}
      {phase === 'solo_upgrade' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:40,padding:20}}>
          <div style={{textAlign:'center',animation:'slideIn 0.5s ease'}}><div style={{fontSize:48,marginBottom:10}}>⚔️</div>
            <h2 style={{fontSize:36,fontWeight:700,color:'#00d4ff',margin:0,letterSpacing:3}}>ROUND {currentRound}</h2>
            <p style={{color:'#7890a0',marginTop:10,fontSize:16}}>Choose an upgrade before battle</p></div>
          <div style={{display:'flex',gap:30,flexWrap:'wrap',justifyContent:'center'}}>
            {upgradeChoices.map((u,i)=>(<div key={u.id} className="ucard" onClick={()=>selSoloUpgrade(u)} style={{animation:'slideIn 0.5s ease '+(0.1*i)+'s both'}}>
              <div style={{fontSize:48,marginBottom:15,textAlign:'center'}}>{u.icon}</div>
              <h3 style={{fontSize:20,fontWeight:700,color:'#00d4ff',margin:'0 0 10px',textAlign:'center'}}>{u.name}</h3>
              <p style={{fontSize:14,color:'#7890a0',margin:0,textAlign:'center',fontFamily:"'Rajdhani',sans-serif"}}>{u.desc}</p></div>))}</div>
          {getUps().length>0&&(<div style={{display:'flex',gap:10,alignItems:'center',animation:'slideIn 0.5s ease 0.3s both',flexWrap:'wrap',justifyContent:'center'}}><span style={{color:'#7890a0',fontSize:14}}>Current:</span>{getUps().map(upBadge)}</div>)}
        </div>
      )}

      {/* SOLO COUNTDOWN */}
      {phase === 'solo_countdown' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
          <div style={{fontSize:150,fontWeight:900,color:'#00d4ff',textShadow:'0 0 60px rgba(0,212,255,0.8)',animation:'countdown 1s ease infinite'}}>{countdown}</div>
          <p style={{fontSize:24,color:'#7890a0',marginTop:20}}>Round {currentRound} — {matchRef.current?.player1.name} vs {matchRef.current?.player2.name}</p>
        </div>
      )}

      {/* SOLO BATTLE */}
      {phase === 'solo_battle' && (
        <Arena tanks={oTanks} bullets={oBullets} mines={oMines} shields={oShields} explosions={oExplosions} obstacles={oObs}
          cvRef={cvRef} battleWinner={battleWinner} myId="player" playerUps={getUps()} currentRound={currentRound} pBC={oPBC} pMB={oPMB} />
      )}

      {/* SOLO VICTORY */}
      {phase === 'solo_victory' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20,textAlign:'center'}}>
          <div style={{animation:'slideIn 0.5s ease'}}><div style={{fontSize:80,marginBottom:20}}>🏆</div>
            <h1 style={{fontSize:48,fontWeight:900,background:'linear-gradient(135deg,#ffd700,#ffaa00)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',margin:0}}>TOURNAMENT CHAMPION!</h1>
            <p style={{color:'#7890a0',marginTop:15,fontSize:18}}>Congratulations, {playerName}!</p></div>
          <BracketDisplay bracket={tournament?.bracket} roundHistory={tournament?.roundHistory} round={currentRound} champion={tournament?.champion} playerName={playerName} />
          <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center',maxWidth:500}}>{getUps().map(upBadge)}</div>
          <button className="btn" onClick={()=>{setPhase('menu');setTournament(null);}}>Play Again</button></div>
      )}

      {/* SOLO DEFEAT */}
      {phase === 'solo_defeat' && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'100vh',gap:30,padding:20,textAlign:'center'}}>
          <div style={{animation:'slideIn 0.5s ease'}}><div style={{fontSize:80,marginBottom:20}}>💥</div>
            <h1 style={{fontSize:48,fontWeight:900,color:'#e74c3c',margin:0}}>DEFEATED</h1>
            <p style={{color:'#7890a0',marginTop:15,fontSize:18}}>Eliminated in Round {currentRound}</p></div>
          <BracketDisplay bracket={tournament?.bracket} roundHistory={tournament?.roundHistory} round={currentRound} champion={tournament?.champion} playerName={playerName} />
          <button className="btn" onClick={()=>{setPhase('menu');setTournament(null);}}>Try Again</button></div>
      )}
    </div>
  );
}
